/**
 * Authentication Provider component for Microsoft Entra ID
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { msalConfig, loginRequest, isAuthEnabled } from './authConfig';
import { API_BASE } from './apiBase';

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL
msalInstance.initialize().catch(console.error);

// Auth context for easy access to auth state
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Inner provider that uses MSAL hooks
function AuthProviderInner({ children }) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({
    can_view: false,
    can_remediate: false,
    can_configure: false,
    role: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get the current account
  const account = accounts[0];

  // Update user state when account changes
  useEffect(() => {
    if (account) {
      setUser({
        name: account.name,
        email: account.username,
        id: account.localAccountId,
      });
    } else {
      setUser(null);
      setPermissions({
        can_view: false,
        can_remediate: false,
        can_configure: false,
        role: null
      });
    }
    setLoading(false);
  }, [account]);

  // Login function
  const login = useCallback(async () => {
    try {
      setError(null);
      await instance.loginPopup(loginRequest);
    } catch (err) {
      console.error('Login failed:', err);
      setError(err.message);
    }
  }, [instance]);

  // Logout function
  const logout = useCallback(async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (err) {
      console.error('Logout failed:', err);
    }
  }, [instance]);

  // Get ID token for API calls to our backend
  // We use ID token (not access token) because:
  // - Access tokens from loginRequest are for Microsoft Graph, not our API
  // - ID tokens always have our app's Client ID as the audience
  const getAccessToken = useCallback(async () => {
    if (!account) {
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });
      // Use idToken for our own backend API authentication
      return response.idToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Token expired or requires interaction, try popup
        try {
          const response = await instance.acquireTokenPopup(loginRequest);
          return response.idToken;
        } catch (popupErr) {
          console.error('Token acquisition failed:', popupErr);
          return null;
        }
      }
      console.error('Token acquisition failed:', err);
      return null;
    }
  }, [instance, account]);

  // Force token refresh - tries silent refresh first, then falls back to popup
  const forceTokenRefresh = useCallback(async () => {
    if (!account) {
      return null;
    }
    try {
      // First try silent refresh with forceRefresh flag
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
        forceRefresh: true, // Force token refresh from server
      });
      return response.idToken;
    } catch (silentErr) {
      console.log('[AuthProvider] Silent refresh failed, trying popup...');
      // If silent refresh fails (e.g., refresh token expired), try popup
      try {
        const response = await instance.acquireTokenPopup({
          ...loginRequest,
          account: account,
        });
        return response.idToken;
      } catch (popupErr) {
        console.error('[AuthProvider] Token refresh popup failed:', popupErr);
        return null;
      }
    }
  }, [instance, account]);

  // Authenticated fetch wrapper with automatic retry on 401
  const authFetch = useCallback(async (url, options = {}) => {
    if (!isAuthEnabled()) {
      // Auth disabled, make regular fetch
      return fetch(url, options);
    }

    const token = await getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
    };

    const response = await fetch(url, { ...options, headers });

    // If we get a 401, try to get a fresh token and retry once
    if (response.status === 401) {
      console.log('[AuthProvider] Got 401, attempting token refresh...');

      // Try to get a fresh token
      const freshToken = await forceTokenRefresh();
      if (freshToken) {
        console.log('[AuthProvider] Token refreshed, retrying request...');
        const retryHeaders = {
          ...options.headers,
          'Authorization': `Bearer ${freshToken}`,
        };
        return fetch(url, { ...options, headers: retryHeaders });
      }

      // If we couldn't get a fresh token, return the original 401 response
      console.log('[AuthProvider] Could not refresh token');
    }

    return response;
  }, [getAccessToken, forceTokenRefresh]);

  // Fetch user permissions from the backend
  const fetchPermissions = useCallback(async () => {
    if (!isAuthenticated || !account) return;

    try {
      const token = await getAccessToken();
      if (!token) return;

      const API_URL = API_BASE;
      const response = await fetch(`${API_URL}/auth/user`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.permissions) {
          setPermissions(data.permissions);
        }
      }
    } catch (err) {
      console.error('[AuthProvider] Error fetching permissions:', err);
    }
  }, [isAuthenticated, account, getAccessToken]);

  // Fetch permissions when authenticated
  useEffect(() => {
    if (isAuthenticated && account) {
      fetchPermissions();
    }
  }, [isAuthenticated, account, fetchPermissions]);

  const value = {
    user,
    permissions,
    isAuthenticated: isAuthEnabled() ? isAuthenticated : true,
    loading,
    error,
    login,
    logout,
    getAccessToken,
    authFetch,
    fetchPermissions,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ===========================================
// BREAK-GLASS EMERGENCY ACCESS
// ===========================================
// Reached via ?sso=false. This exists for the case where Entra sign-in cannot
// work at all — including a broken app registration, an expired secret, or
// revoked consent — so the entire break-glass path is kept OUTSIDE MsalProvider.
// If MSAL cannot initialize, this must still render.
//
// No secret lives in the frontend. The password is verified by the API against
// an Argon2id hash held on the API host; this component only collects it and
// exchanges it for a short-lived token.

// API_BASE is imported from ./apiBase, which normalizes the /api suffix.

// sessionStorage, matching msalConfig.cache — cleared when the tab closes, and
// never localStorage, which would outlive the emergency it was opened for.
const BREAKGLASS_STORAGE_KEY = 'assetmesh.breakglass.session';

export function isBreakglassRequested() {
  try {
    return new URLSearchParams(window.location.search).get('sso') === 'false';
  } catch {
    return false;
  }
}

function loadStoredBreakglassSession() {
  try {
    const raw = sessionStorage.getItem(BREAKGLASS_STORAGE_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (!session?.token || !session?.expiresAt) return null;
    // Treat an already-expired stored session as absent.
    if (new Date(session.expiresAt).getTime() <= Date.now()) {
      sessionStorage.removeItem(BREAKGLASS_STORAGE_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

function storeBreakglassSession(session) {
  try {
    if (session) {
      sessionStorage.setItem(BREAKGLASS_STORAGE_KEY, JSON.stringify(session));
    } else {
      sessionStorage.removeItem(BREAKGLASS_STORAGE_KEY);
    }
  } catch {
    /* storage unavailable — session stays in memory for this page load */
  }
}

function formatRemaining(ms) {
  if (ms <= 0) return '0:00';
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

function BreakglassBanner({ expiresAt, onExit }) {
  const [remaining, setRemaining] = useState(
    () => new Date(expiresAt).getTime() - Date.now()
  );

  useEffect(() => {
    const id = setInterval(
      () => setRemaining(new Date(expiresAt).getTime() - Date.now()),
      1000
    );
    return () => clearInterval(id);
  }, [expiresAt]);

  return (
    <div style={styles.banner}>
      <span>
        <strong>⚠ Break-glass session</strong> — emergency Admin access. This use
        has been recorded in the audit log.
      </span>
      <span style={styles.bannerRight}>
        Expires in <strong>{formatRemaining(remaining)}</strong>
        <button onClick={onExit} style={styles.bannerButton}>
          End session
        </button>
      </span>
    </div>
  );
}

function BreakglassAuthProvider({ session, setSession, children }) {
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const endSession = useCallback(() => {
    storeBreakglassSession(null);
    setSession(null);
  }, [setSession]);

  // Expire the session client-side at the moment the token dies, so the UI
  // cannot keep presenting an Admin view backed by a token the API rejects.
  useEffect(() => {
    if (!session?.expiresAt) return undefined;
    const ms = new Date(session.expiresAt).getTime() - Date.now();
    if (ms <= 0) {
      endSession();
      return undefined;
    }
    const id = setTimeout(endSession, ms);
    return () => clearTimeout(id);
  }, [session, endSession]);

  const breakglassLogin = useCallback(async (password) => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/auth/breakglass`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      if (response.status === 404) {
        setError(
          'Emergency access is unavailable on this server. Either BREAKGLASS_PASSWORD_HASH is not set on the API host, or the API is not running a build that includes break-glass.'
        );
        return false;
      }
      if (response.status === 429) {
        setError('Too many attempts. Emergency access is rate-limited — wait and try again.');
        return false;
      }
      if (!response.ok) {
        let detail = null;
        try {
          detail = (await response.json())?.error;
        } catch {
          /* no JSON body — fall through to the generic message */
        }

        // An API running a build without break-glass has no /api/auth/breakglass
        // in its middleware skip list, so the request is rejected as
        // unauthenticated *before* it ever reaches the endpoint. That 401 is
        // otherwise indistinguishable from a wrong password, which sends an
        // operator hunting a credential problem that does not exist — during an
        // outage, which is the worst possible time. The middleware's message
        // differs from the endpoint's, so use it to tell them apart.
        if (detail === 'Authentication required') {
          setError(
            'This API does not have break-glass deployed. The request was rejected by authentication middleware before reaching the endpoint — deploy the current backend, then retry.'
          );
          return false;
        }

        // Genuine credential failure. Not surfacing server detail — the API
        // returns the same generic message for every one.
        setError('Invalid credentials.');
        return false;
      }

      const data = await response.json();
      const next = { token: data.access_token, expiresAt: data.expires_at };
      storeBreakglassSession(next);
      setSession(next);
      return true;
    } catch (err) {
      setError(
        `Could not reach the API at ${API_BASE}. Check that the backend is running and REACT_APP_API_URL is correct.`
      );
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [setSession]);

  const authFetch = useCallback(async (url, options = {}) => {
    if (!session?.token) throw new Error('Not authenticated');
    const response = await fetch(url, {
      ...options,
      headers: { ...options.headers, Authorization: `Bearer ${session.token}` },
    });
    // No refresh path by design — a rejected token means the session is over.
    if (response.status === 401) endSession();
    return response;
  }, [session, endSession]);

  const value = {
    user: session
      ? { name: 'Break-glass Admin', email: 'breakglass@local', id: 'breakglass' }
      : null,
    permissions: session
      ? { can_view: true, can_remediate: true, can_configure: true, role: 'Admin' }
      : { can_view: false, can_remediate: false, can_configure: false, role: null },
    isAuthenticated: Boolean(session),
    loading: false,
    error,
    submitting,
    login: () => {},
    logout: endSession,
    getAccessToken: async () => session?.token ?? null,
    authFetch,
    fetchPermissions: () => {},
    isBreakglass: true,
    breakglassLogin,
    breakglassExpiresAt: session?.expiresAt ?? null,
  };

  return (
    <AuthContext.Provider value={value}>
      {session && (
        <BreakglassBanner expiresAt={session.expiresAt} onExit={endSession} />
      )}
      {children}
    </AuthContext.Provider>
  );
}

// Break-glass login form
export function BreakglassLoginPage() {
  const { breakglassLogin, error, submitting } = useAuth();
  const [password, setPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || submitting) return;
    await breakglassLogin(password);
    // Clear the field either way so the value does not linger in memory or in
    // a re-rendered DOM node after a failed attempt.
    setPassword('');
  };

  return (
    <div style={styles.container}>
      <form style={{ ...styles.card, ...styles.breakglassCard }} onSubmit={handleSubmit}>
        <h1 style={styles.title}>Asset Mesh</h1>
        <div style={styles.breakglassLabel}>Emergency Access</div>
        <p style={styles.subtitle}>
          Single sign-on is bypassed. Use this only when Entra ID sign-in is
          unavailable — every attempt is recorded in the audit log.
        </p>
        {error && <p style={styles.error}>{error}</p>}
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Break-glass password"
          autoComplete="off"
          autoFocus
          disabled={submitting}
          style={styles.input}
          aria-label="Break-glass password"
        />
        <button
          type="submit"
          disabled={submitting || !password}
          style={{
            ...styles.button,
            ...styles.breakglassButton,
            ...(submitting || !password ? styles.buttonDisabled : {}),
          }}
        >
          {submitting ? 'Verifying…' : 'Unlock emergency access'}
        </button>
        <a href={window.location.pathname} style={styles.link}>
          Back to normal sign-in
        </a>
      </form>
    </div>
  );
}

// Main AuthProvider that wraps with MSAL
export function AuthProvider({ children }) {
  // Read once on mount so a stored session survives re-renders.
  const [breakglassSession, setBreakglassSession] = useState(
    loadStoredBreakglassSession
  );

  // Checked first, ahead of the auth-disabled development bypass: ?sso=false is
  // an explicit request for the emergency door and must be honored in every
  // configuration, including locally where it is the only way to exercise the
  // flow. Rendered outside MsalProvider on purpose — it has to work when the
  // Entra integration itself is the thing that is broken.
  if (breakglassSession || isBreakglassRequested()) {
    return (
      <BreakglassAuthProvider
        session={breakglassSession}
        setSession={setBreakglassSession}
      >
        {children}
      </BreakglassAuthProvider>
    );
  }

  if (!isAuthEnabled()) {
    // Auth disabled, provide mock context with full Admin permissions
    const mockValue = {
      user: { name: 'Development User', email: 'dev@localhost', roles: ['Admin'] },
      permissions: {
        can_view: true,
        can_remediate: true,
        can_configure: true,
        role: 'Admin'
      },
      isAuthenticated: true,
      loading: false,
      error: null,
      login: () => {},
      logout: () => {},
      getAccessToken: async () => null,
      authFetch: (url, options) => fetch(url, options),
      fetchPermissions: () => {},
    };

    return (
      <AuthContext.Provider value={mockValue}>
        {children}
      </AuthContext.Provider>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </MsalProvider>
  );
}

// Login page component
export function LoginPage() {
  const { login, error, loading } = useAuth();

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Asset Mesh</h1>
        <p style={styles.subtitle}>Sign in to continue</p>
        {error && <p style={styles.error}>{error}</p>}
        <button onClick={login} style={styles.button}>
          Sign in with Microsoft
        </button>
      </div>
    </div>
  );
}

// Protected route wrapper
export function RequireAuth({ children }) {
  const { isAuthenticated, loading, isBreakglass } = useAuth();

  // Break-glass is checked before the isAuthEnabled() shortcut: ?sso=false must
  // still present the password prompt rather than falling through to the
  // no-auth development bypass.
  if (isBreakglass) {
    return isAuthenticated ? children : <BreakglassLoginPage />;
  }

  if (!isAuthEnabled()) {
    return children;
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return children;
}

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  card: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    textAlign: 'center',
    maxWidth: '400px',
  },
  title: {
    margin: '0 0 10px 0',
    color: '#333',
  },
  subtitle: {
    margin: '0 0 30px 0',
    color: '#666',
  },
  button: {
    backgroundColor: '#0078d4',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '4px',
    fontSize: '16px',
    cursor: 'pointer',
  },
  error: {
    color: '#d32f2f',
    marginBottom: '20px',
  },
  // --- Break-glass ---
  // Visually distinct from the normal sign-in on purpose: an operator should
  // never be unsure which door they came in through.
  breakglassCard: {
    display: 'flex',
    flexDirection: 'column',
    borderTop: '4px solid #b45309',
    minWidth: '340px',
  },
  breakglassLabel: {
    display: 'inline-block',
    alignSelf: 'center',
    backgroundColor: '#fef3c7',
    color: '#92400e',
    border: '1px solid #f59e0b',
    borderRadius: '999px',
    padding: '2px 12px',
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    marginBottom: '16px',
  },
  input: {
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    marginBottom: '16px',
    width: '100%',
    boxSizing: 'border-box',
  },
  breakglassButton: {
    backgroundColor: '#b45309',
    width: '100%',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  link: {
    marginTop: '18px',
    fontSize: '13px',
    color: '#666',
  },
  banner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
    backgroundColor: '#b45309',
    color: 'white',
    padding: '8px 16px',
    fontSize: '13px',
    position: 'sticky',
    top: 0,
    zIndex: 9999,
  },
  bannerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  bannerButton: {
    backgroundColor: 'rgba(0,0,0,0.25)',
    color: 'white',
    border: '1px solid rgba(255,255,255,0.4)',
    borderRadius: '4px',
    padding: '4px 10px',
    fontSize: '12px',
    cursor: 'pointer',
  },
};

export default AuthProvider;
